import type { Metadata } from "next";
import { withDefaultOgImage } from "@/lib/siteMetadata";

export type ServicePageData = {
  slug: string;
  metadata: Metadata;
  eyebrow: string;
  headline: string;
  headlineAccent: string;
  subtitle: string;
  heroImage: string;
  features: { title: string; desc: string }[];
  process: { step: string; title: string; desc: string }[];
};

function meta(title: string, description: string, slug: string): Metadata {
  const url = `https://doimpo.com/services/${slug}`;
  return withDefaultOgImage({
    title,
    description,
    alternates: { canonical: url },
    openGraph: {
      title,
      description,
      url,
      siteName: "doimpo",
      type: "website",
    },
    twitter: { title, description },
  });
}

export const SERVICE_PAGES: Record<string, ServicePageData> = {
  "strategy-consulting": {
    slug: "strategy-consulting",
    metadata: meta(
      "Strategy & Consulting | doimpo",
      "Growth roadmaps, positioning, and vendor consolidation—one partner runs strategy through execution.",
      "strategy-consulting"
    ),
    eyebrow: "Strategy & consulting",
    headline: "One playbook.",
    headlineAccent: "No vendor spaghetti.",
    subtitle:
      "Roadmaps and positioning your team can execute—without three agencies rewriting the brief.",
    heroImage: "/media/images/services/strategy.webp",
    features: [
      { title: "Digital Strategy & Roadmapping", desc: "Prioritized initiatives with owners and timelines." },
      { title: "Growth Strategy", desc: "Funnel math and channel bets tied to revenue goals." },
      { title: "Vision Alignment", desc: "One narrative every team and vendor follows." },
      { title: "Vendor Consolidation", desc: "Cut the coordination tax; keep the specialists you need." },
    ],
    process: [
      { step: "01", title: "Listen & assess", desc: "Business model, constraints, and current stack." },
      { step: "02", title: "Design", desc: "A strategy your operators can run without us in the room." },
      { step: "03", title: "Execute", desc: "We coordinate brand, product, and growth under that plan." },
    ],
  },
  "branding-design": {
    slug: "branding-design",
    metadata: meta(
      "Branding & Design | doimpo",
      "Brand strategy, visual identity, and design systems—built by the same partner who ships your site and campaigns.",
      "branding-design"
    ),
    eyebrow: "Branding & design",
    headline: "Identity that sells",
    headlineAccent: "before the pitch starts.",
    subtitle:
      "Positioning, visual system, and guidelines your site and sales decks can use tomorrow.",
    heroImage: "/media/images/services/branding.webp",
    features: [
      { title: "Brand Strategy", desc: "Positioning, voice, and proof hierarchy for buyers." },
      { title: "Visual Identity", desc: "Color, type, and components that hold up in product UI." },
      { title: "Brand Guidelines", desc: "Rules your team and freelancers can follow without a call." },
      { title: "Brand Refresh", desc: "Modernize without burning recognition you already earned." },
    ],
    process: [
      { step: "01", title: "Discovery", desc: "Audience, rivals, and what must stay vs go." },
      { step: "02", title: "Design", desc: "Concepts, critique, and refinement until it lands." },
      { step: "03", title: "Deliver", desc: "Assets and guidelines ready for web, deck, and social." },
    ],
  },
  "digital-marketing": {
    slug: "digital-marketing",
    metadata: meta(
      "Digital Marketing | doimpo",
      "SEO, paid media, content, and social—one partner who already knows your brand and offer.",
      "digital-marketing"
    ),
    eyebrow: "Digital marketing",
    headline: "Channels that share",
    headlineAccent: "one story.",
    subtitle:
      "SEO, paid, content, and social run from the same brief—not four agencies with four narratives.",
    heroImage: "/media/images/services/marketing.webp",
    features: [
      { title: "SEO & Content", desc: "Pages and posts that rank and convert." },
      { title: "Paid Media", desc: "Google, Meta, and LinkedIn spend tied to pipeline." },
      { title: "Social Strategy", desc: "Platform-native posts that match your brand voice." },
      { title: "Conversion Optimization", desc: "Tests that improve the funnel you already paid for." },
    ],
    process: [
      { step: "01", title: "Audit & plan", desc: "Channels, competitors, and a calendar you can keep." },
      { step: "02", title: "Launch", desc: "Campaigns live against brand and revenue goals." },
      { step: "03", title: "Optimize", desc: "Cut losers, scale winners, report in plain English." },
    ],
  },
  "web-development": {
    slug: "web-development",
    metadata: meta(
      "Web Development | doimpo",
      "Marketing sites and web apps built by the same team that owns your brand and growth plan.",
      "web-development"
    ),
    eyebrow: "Web development",
    headline: "Sites that load fast",
    headlineAccent: "and close the deal.",
    subtitle:
      "Conversion-ready sites and apps—Core Web Vitals, SEO, and brand baked in from day one.",
    heroImage: "/media/images/services/web-dev.webp",
    features: [
      { title: "Marketing Sites", desc: "Fast, SEO-ready sites that convert visitors." },
      { title: "Web Applications", desc: "Custom apps with modern frameworks and APIs." },
      { title: "Performance & SEO", desc: "Core Web Vitals and technical SEO from the start." },
      { title: "Maintenance & Scale", desc: "Support as traffic and features grow." },
    ],
    process: [
      { step: "01", title: "Scope & design", desc: "Requirements, architecture, and UX aligned to brand." },
      { step: "02", title: "Build", desc: "Develop, test, and integrate with your stack." },
      { step: "03", title: "Ship", desc: "Launch, monitor, and iterate after release." },
    ],
  },
  "mobile-app-development": {
    slug: "mobile-app-development",
    metadata: meta(
      "Mobile App Development | doimpo",
      "iOS, Android, and cross-platform apps—designed and shipped without vendor handoffs.",
      "mobile-app-development"
    ),
    eyebrow: "Mobile app development",
    headline: "Apps people open",
    headlineAccent: "more than once.",
    subtitle:
      "Native or cross-platform builds from one team—design through store submission.",
    heroImage: "/media/images/services/app-dev.webp",
    features: [
      { title: "iOS & Android", desc: "Native apps tuned for each platform." },
      { title: "Cross-Platform", desc: "React Native or Flutter when speed to market wins." },
      { title: "UX/UI Design", desc: "Mobile-first screens that match your brand system." },
      { title: "Post-Launch Support", desc: "Updates, analytics, and the next feature slice." },
    ],
    process: [
      { step: "01", title: "Define & design", desc: "Features, users, and UI locked to brand." },
      { step: "02", title: "Develop", desc: "Build, test, and wire backends." },
      { step: "03", title: "Launch", desc: "Store submission and growth activation." },
    ],
  },
  "ai-solutions": {
    slug: "ai-solutions",
    metadata: meta(
      "AI Solutions | doimpo",
      "Agents, OCR, and workflow automation wired into the stack you already run.",
      "ai-solutions"
    ),
    eyebrow: "AI solutions",
    headline: "Kill the copy-paste",
    headlineAccent: "busywork.",
    subtitle:
      "WhatsApp agents, document extraction, and internal copilots—auditable and production-ready.",
    heroImage: "/media/images/services/ai-solutions.webp",
    features: [
      { title: "AI Strategy", desc: "Find the workflows where automation pays back fastest." },
      { title: "Workflow Automation", desc: "Connect tools and retire repetitive handoffs." },
      { title: "Custom AI Agents", desc: "Agents trained on your processes and knowledge base." },
      { title: "Security & Governance", desc: "Access controls, audit trails, and human review gates." },
    ],
    process: [
      { step: "01", title: "Discover", desc: "Map workflows and pick high-ROI automations." },
      { step: "02", title: "Build", desc: "Ship agents, automations, and APIs." },
      { step: "03", title: "Scale", desc: "Monitor, refine, and expand use cases." },
    ],
  },
  "content-creation": {
    slug: "content-creation",
    metadata: meta(
      "Content Creation | doimpo",
      "Photography, video, and copy produced in-house and aligned to your brand and campaigns.",
      "content-creation"
    ),
    eyebrow: "Content creation",
    headline: "Assets that match",
    headlineAccent: "the brand system.",
    subtitle:
      "Photo, video, and copy from the same team that owns your identity—not a freelancers' free-for-all.",
    heroImage: "/media/images/services/content.webp",
    features: [
      { title: "Product Photography", desc: "Studio and lifestyle shoots for commerce and brand." },
      { title: "Video Production", desc: "Promos, explainers, and social-ready clips." },
      { title: "Copywriting", desc: "Web, blog, and campaign messaging that converts." },
      { title: "Post-Production", desc: "Edit, retouch, and deliver ready-to-publish assets." },
    ],
    process: [
      { step: "01", title: "Brief", desc: "Goals, audience, and creative direction." },
      { step: "02", title: "Produce", desc: "Shoot, write, and build the assets." },
      { step: "03", title: "Distribute", desc: "Publish across web, social, and campaigns." },
    ],
  },
  "ecommerce-solutions": {
    slug: "ecommerce-solutions",
    metadata: meta(
      "E-commerce Solutions | doimpo",
      "Storefronts, checkout, and conversion work—design, build, and growth under one partner.",
      "ecommerce-solutions"
    ),
    eyebrow: "E-commerce solutions",
    headline: "Stores that sell",
    headlineAccent: "past the cart.",
    subtitle:
      "Shopify, WooCommerce, or custom—payments, UX, and optimization from one team.",
    heroImage: "/media/images/services/ecommerce.webp",
    features: [
      { title: "Platform Setup", desc: "Shopify, WooCommerce, or custom—fit to your model." },
      { title: "Custom Store Development", desc: "Branded experiences without theme dead ends." },
      { title: "Payment Integration", desc: "Stripe, PayPal, and multi-currency when you need it." },
      { title: "Conversion Optimization", desc: "Tests and analytics aimed at revenue, not vanity." },
    ],
    process: [
      { step: "01", title: "Discovery", desc: "Catalog, customers, and growth goals." },
      { step: "02", title: "Build", desc: "Design and ship a secure store." },
      { step: "03", title: "Grow", desc: "Launch, optimize, and scale revenue." },
    ],
  },
};

export function getServicePage(slug: string): ServicePageData {
  const page = SERVICE_PAGES[slug];
  if (!page) throw new Error(`Unknown service slug: ${slug}`);
  return page;
}
